import {PickUpListService} from '../shared/pick-up-list.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {Component, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Reservation} from '../../models/reservation';
import {ReservationDialogComponent} from '../reservation-dialog/reservation-dialog.component';
import {MatDialog} from '@angular/material';
import {CuiBaseComponent} from '@cicero/webui/lib/core/shared/base-component';
import {ReservationService} from '../shared/reservation.service';
import {Observable} from 'rxjs/Observable';
import {environment} from '../../../environments/environment';
import {animate, style, transition, trigger} from '@angular/animations';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs/Subject';
import {MaterialService} from '../../material/material.service';

const animationDuration = 200;

@Component({
  selector: 'app-pick-up-list',
  templateUrl: './pick-up-list.component.html',
  styleUrls: ['./pick-up-list.component.scss'],
  animations: [
    trigger('flyOut', [
      transition('* => hidden', [
        style({transform: 'translateX(0)'}),
        animate(
          `${animationDuration / 2}ms ${animationDuration / 2}ms cubic-bezier(0.4, 0.0, 0.2, 1)`,
          style({opacity: 0, transform: 'translateX(100%)'}))
      ])
    ])
  ]
})
export class PickUpListComponent extends CuiBaseComponent implements OnInit {
  reservations = new BehaviorSubject<Reservation[]>([]);
  visibleReservations: Reservation[] = [];

  displayedReservations = new BehaviorSubject<Reservation[]>([]);
  displayedReservationHeights: number | number[];

  queryDescriptor = new BehaviorSubject<string>('');
  showQueryDescriptor = false;

  reservationStateChange = new Subject();
  sortByReservationDate = new FormControl(false);
  hideHandled = new FormControl(false);
  showDetails = new FormControl(false);

  hasLoaded = false;
  error?: string;

  get isOffline() {
    return !window.navigator.onLine;
  }

  constructor(public route: ActivatedRoute,
              public router: Router,
              public pickUpList: PickUpListService,
              public materialService: MaterialService,
              public dialog: MatDialog,
              public reservationService: ReservationService) {
    super();
  }

  ngOnInit() {
    this.pushSubscription(
      this.startReservationStatePolling(),
      this.setupReservationFetching(),
      this.setupReservationSorting(),
      this.setupReservationFiltering(),
      this.setupReservationDetails()
    );
  }

  startReservationStatePolling() {
    if (environment.disableLongRunningTasks) {
      return Observable.empty().subscribe();
    }

    return Observable.interval(environment.pickUpListPollingInterval)
      .switchMap(() => this.pickUpList.updateReservationStates(this.reservations.getValue()))
      .subscribe(reservations => this.reservations.next(reservations));
  }

  setupReservationFetching() {
    return this.route.queryParams
      .do(() => this.hasLoaded = false)
      .switchMap((queryParams: Params) => this.pickUpList.search(queryParams))
      .map(result => Object.assign(result, {reservations: this.sortReservations(result.reservations)}))
      .do(() => this.hasLoaded = true)
      .subscribe(
        result => {
          this.reservations.next(result.reservations);
          this.queryDescriptor.next(result.query);
          this.showQueryDescriptor = true;

          this.materialService.getCovers(result.reservations.map(r => r.faustNumber))
            .map(covers => result.reservations.map(r => Object.assign(r, {cover: covers[r.faustNumber].detail})))
            .subscribe(reservations => this.reservations.next(reservations));
        },
        () => this.error = 'Noget gik galt da vi hentede søgeresultaterne.'
      );
  }

  setupReservationSorting() {
    return this.sortByReservationDate.valueChanges
      .subscribe(() => this.reservations.next(this.sortReservations(this.reservations.getValue())));
  }

  setupReservationFiltering() {
    return Observable.merge(
      this.hideHandled.valueChanges,
      this.reservationStateChange.filter(() => this.hideHandled.value).delay(animationDuration),
      this.reservations
    )
      .map(() => this.reservations.getValue())
      .map(reservations => this.filterReservations(reservations))
      .subscribe(reservations => {
        this.displayedReservations.next(reservations);
        this.displayedReservationHeights = this.getReservationHeights(reservations);
      });
  }

  setupReservationDetails() {
    return this.showDetails.valueChanges
      .subscribe(() => this.displayedReservationHeights = this.getReservationHeights(this.displayedReservations.getValue()));
  }


  getReservationHeights(reservations: Reservation[]) {
    /**
     NOTICE: The following constants depends on the height of the reservation component.
     This method can predict the painted height of the reservation component based on its data.
     */
    const contentHeight = 186;
    const hrHeight = 2;
    const detailsHeight = 71;
    const materialHeight = 17;

    if (!this.showDetails.value) {
      return contentHeight + hrHeight;
    }

    return reservations.reduce((heights, reservation) => {
      let height = contentHeight + hrHeight + detailsHeight + materialHeight * reservation.materials.length;

      if (heights.length) {
        height += heights[heights.length - 1];
      }

      heights.push(height);

      return heights;
    }, []);
  }

  sortReservations(reservations: Reservation[]): Reservation[] {
    return this.sortByReservationDate.value
      ? this.pickUpList.sortByReservationAge(reservations)
      : this.pickUpList.sort(reservations);
  }

  filterReservations(reservations: Reservation[]): Reservation[] {
    if (!this.hideHandled.value) {
      return reservations;
    }

    return reservations.filter(reservation => !reservation.state);
  }

  showDialog(reservation: Reservation): void {
    const dialog = this.dialog.open(ReservationDialogComponent, {
      data: reservation,
      backdropClass: 'no-overflow-no-padding-backdrop'
    });

    dialog.componentInstance.reservationStateChange.subscribe(() => this.reservationStateChange.next());
  }

  undo(reservation: Reservation) {
    this.reservationService.undo(reservation).subscribe();
    this.reservationStateChange.next();
  }

  markSkipped(reservation: Reservation) {
    this.reservationService.markSkipped(reservation);
    this.reservationStateChange.next();
  }

  markFound(reservation: Reservation) {
    this.reservationService.markFound(reservation).subscribe();
    this.reservationStateChange.next();
  }

  markNotFound(reservation: Reservation) {
    this.reservationService.markNotFound(reservation).subscribe(null, () => this.reservationStateChange.next());
    this.reservationStateChange.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/pick-up-list/pick-up-list/pick-up-list.component.ts